// Прибиращо се странично меню (лява лента с инструменти).
// Отваря се с ☰ от заглавната лента, затваря се с докосване извън него.

import React, { useEffect, useRef, useState } from 'react';
import { Animated, Modal, Pressable, ScrollView, Text, View } from 'react-native';
import { createNavigationContainerRef } from '@react-navigation/native';
import { useAuth } from './AuthContext';
import { COLORS } from './ui';
import { t } from './i18n';

export const navigationRef = createNavigationContainerRef<Record<string, undefined>>();

const WIDTH = 280;

let toggleRef: (() => void) | null = null;
// Бургерът превключва менюто (отваря/затваря)
export function toggleDrawer(): void {
  toggleRef?.();
}

// Функции, а не константи — етикетите се превеждат при всяко рендиране (смяна на езика)
const roleLabels = (): Record<string, string> => ({
  LANDLORD: t('Наемодател'),
  AGENCY: t('Пропърти мениджър'),
  TENANT: t('Наемател'),
  OWNER: t('Собственик'),
  HANDYMAN: t('Майстор'),
  ADMIN: t('Админ'),
  MODERATOR: t('Модератор'),
  CREATOR: t('Криейтър'),
  RESIDENT: t('Обитател'),
});

function getMenus(): Record<string, { icon: string; label: string; screen: string }[]> {
  const MENUS: Record<string, { icon: string; label: string; screen: string }[]> = {
    LANDLORD: [
      { icon: '🏠', label: t('Начало'), screen: 'Dashboard' },
      { icon: '🏢', label: t('Имоти'), screen: 'Apartments' },
      { icon: '🔎', label: t('Свободни апартаменти'), screen: 'Listings' },
      { icon: '💬', label: t('Съобщения'), screen: 'Conversations' },
      { icon: '🛠️', label: t('Нередности'), screen: 'Issues' },
      { icon: '📄', label: t('Документи'), screen: 'Documents' },
      { icon: '🧾', label: t('Фактури'), screen: 'Invoices' },
      { icon: '⭐', label: t('Абонамент'), screen: 'Subscription' },
      { icon: '🔔', label: t('Известия'), screen: 'Notifications' },
      { icon: '👤', label: t('Профил'), screen: 'Profile' },
      { icon: '🔍', label: t('Провери наемател'), screen: 'Reputation' },
    ],
    TENANT: [
      { icon: '🏠', label: t('Моят дом'), screen: 'TenantHome' },
      { icon: '🔎', label: t('Свободни апартаменти'), screen: 'Listings' },
      { icon: '💬', label: t('Съобщения'), screen: 'Conversations' },
      { icon: '🛠️', label: t('Нередности'), screen: 'Issues' },
      { icon: '📄', label: t('Документи'), screen: 'Documents' },
      { icon: '🧾', label: t('Фактури'), screen: 'Invoices' },
      { icon: '🔔', label: t('Известия'), screen: 'Notifications' },
      { icon: '👤', label: t('Профил'), screen: 'Profile' },
      { icon: '🔍', label: t('Провери наемател'), screen: 'Reputation' },
    ],
    OWNER: [
      { icon: '🏢', label: t('Моите имоти'), screen: 'OwnerApartments' },
      { icon: '🛠️', label: t('Нередности'), screen: 'Issues' },
      { icon: '🔔', label: t('Известия'), screen: 'Notifications' },
      { icon: '👤', label: t('Профил'), screen: 'Profile' },
      { icon: '🔍', label: t('Провери наемател'), screen: 'Reputation' },
    ],
    HANDYMAN: [
      { icon: '🔧', label: t('Заявки'), screen: 'HandymanRequests' },
      { icon: '🔔', label: t('Известия'), screen: 'Notifications' },
      { icon: '👤', label: t('Профил'), screen: 'Profile' },
      { icon: '🔍', label: t('Провери наемател'), screen: 'Reputation' },
    ],
    // Обитател: живее в имота, но не е страна по наема — само адрес и нередности
    RESIDENT: [
      { icon: '🏠', label: t('Моят дом'), screen: 'ResidentHome' },
      { icon: '🔔', label: t('Известия'), screen: 'Notifications' },
      { icon: '👤', label: t('Профил'), screen: 'Profile' },
    ],
  };
  MENUS.AGENCY = MENUS.LANDLORD;
  return MENUS;
}

export function DrawerMenu() {
  const { user, signOut } = useAuth();
  const [open, setOpen] = useState(false);
  const slide = useRef(new Animated.Value(-WIDTH)).current;

  useEffect(() => {
    toggleRef = () => setOpen((v) => !v);
    return () => {
      toggleRef = null;
    };
  }, []);

  useEffect(() => {
    Animated.timing(slide, { toValue: open ? 0 : -WIDTH, duration: 180, useNativeDriver: false }).start();
  }, [open, slide]);

  if (!user) return null;
  const MENUS = getMenus();
  const items = MENUS[user.role] ?? MENUS.HANDYMAN;

  const go = (screen: string) => {
    setOpen(false);
    if (navigationRef.isReady()) navigationRef.navigate(screen as never);
  };

  // Modal винаги стои над нативните екрани → докосването извън менюто
  // надеждно го затваря (за разлика от плаващ absolute слой на iOS).
  return (
    <Modal visible={open} transparent animationType="none" onRequestClose={() => setOpen(false)}>
      <Pressable onPress={() => setOpen(false)} style={{ flex: 1, backgroundColor: 'rgba(0,0,0,0.35)' }}>
        <Animated.View
          style={{
            position: 'absolute',
            top: 0,
            bottom: 0,
            left: 0,
            width: WIDTH,
            transform: [{ translateX: slide }],
            backgroundColor: COLORS.card,
            paddingTop: 56,
            paddingHorizontal: 12,
            borderRightWidth: 1,
            borderRightColor: COLORS.border,
            shadowColor: '#000',
            shadowOpacity: 0.15,
            shadowRadius: 12,
            elevation: 12,
          }}
        >
          {/* докосване вътре в менюто не го затваря; скрол за ниски екрани */}
          <ScrollView showsVerticalScrollIndicator={false}>
          <Pressable onPress={() => {}}>
        <View style={{ paddingHorizontal: 12, paddingBottom: 16, borderBottomWidth: 1, borderBottomColor: COLORS.border }}>
          <Text style={{ fontSize: 22, fontWeight: '800', color: COLORS.primary }}>Livupp</Text>
          <Text style={{ fontSize: 15, fontWeight: '600', color: COLORS.text, marginTop: 8 }}>
            {user.firstName} {user.lastName}
          </Text>
          <Text style={{ fontSize: 13, color: COLORS.muted }}>{roleLabels()[user.role]}</Text>
        </View>

        {items.map((item) => (
          <Pressable
            key={item.screen}
            onPress={() => go(item.screen)}
            style={({ pressed }) => ({
              flexDirection: 'row',
              alignItems: 'center',
              gap: 12,
              paddingVertical: 14,
              paddingHorizontal: 12,
              borderRadius: 10,
              backgroundColor: pressed ? COLORS.bg : 'transparent',
            })}
          >
            <Text style={{ fontSize: 18 }}>{item.icon}</Text>
            <Text style={{ fontSize: 16, color: COLORS.text, fontWeight: '500' }}>{item.label}</Text>
          </Pressable>
        ))}

        <Pressable
          onPress={() => {
            setOpen(false);
            signOut();
          }}
          style={{ flexDirection: 'row', alignItems: 'center', gap: 12, paddingVertical: 14, paddingHorizontal: 12, marginTop: 12, borderTopWidth: 1, borderTopColor: COLORS.border }}
        >
          <Text style={{ fontSize: 18 }}>🚪</Text>
          <Text style={{ fontSize: 16, color: COLORS.danger, fontWeight: '600' }}>{t('Изход')}</Text>
        </Pressable>
          </Pressable>
          </ScrollView>
        </Animated.View>
      </Pressable>
    </Modal>
  );
}

export function BurgerButton() {
  return (
    <Pressable onPress={toggleDrawer} style={{ paddingHorizontal: 14, paddingVertical: 6 }} hitSlop={10}>
      <Text style={{ fontSize: 24, color: COLORS.text }}>☰</Text>
    </Pressable>
  );
}
